import { Check, ChevronDown, CircleHelp, Eye, Layers, Redo2, SlidersHorizontal, Spline, Tag, Undo2, X } from "lucide-react";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import { FileMenu } from "./FileMenu.tsx";
import { TimeControl } from "./TimeControl.tsx";
import { AccountButton } from "./Account.tsx";
import { CollabButton, InviteButton } from "./Share.tsx";
import { BrandMark, WorldTile } from "./Brand.tsx";
import { usePopover } from "./usePopover.ts";

const DIMS: Record<string, { label: string; color: string }> = {
  "minecraft:overworld": { label: "Overworld", color: "var(--accent)" },
  "minecraft:the_nether": { label: "Nether", color: "#e8705a" },
  "minecraft:the_end": { label: "The End", color: "var(--violet)" },
};

export function dimensionInfo(id: string) {
  return DIMS[id] ?? { label: id.replace(/^[a-z0-9_.-]+:/, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()), color: "var(--fg-faint)" };
}

/** The window's menubar: brand + world, dimension, the view switches in the middle, actions on the right. */
export function TopBar() {
  const readOnly = useApp((s) => s.readOnly);
  const paused = useApp((s) => !!s.room?.paused);
  const helpOpen = useApp((s) => s.helpOpen);
  const collabOpen = useApp((s) => s.collabOpen);
  const set = useApp((s) => s.set);

  return (
    <header className="topbar menubar">
      <div className="mb-lead">
        <BrandMark title="MC Planner" />
        <WorldMenu />
      </div>
      <DimensionMenu />

      <div className="spacer" />
      <ViewBar />
      <div className="spacer" />

      <div className="mb-actions">
        {readOnly && (
          <span className="view-only chip chip-amber" title={paused ? "The owner's plan doesn't cover this world any more: you can look around and export, not edit." : "You're a viewer in this shared world: you can look around, not change it."}>
            <Eye size={13} aria-hidden="true" />{paused ? "Paused · view only" : "View only"}
          </span>
        )}
        <button className="btn btn-ghost btn-icon" onClick={() => controller.undo()} title="Undo · Ctrl Z" aria-label="Undo"><Undo2 size={16} aria-hidden="true" /></button>
        <button className="btn btn-ghost btn-icon" onClick={() => controller.redo()} title="Redo · Ctrl Shift Z" aria-label="Redo"><Redo2 size={16} aria-hidden="true" /></button>
        <FileMenu />
        <button className="btn btn-ghost btn-icon help-btn" aria-pressed={helpOpen} onClick={() => set({ helpOpen: !helpOpen })} title="Shortcuts & help · ?" aria-label="Shortcuts and help"><CircleHelp size={16} aria-hidden="true" /></button>
        <span className="mb-sep" aria-hidden="true" />
        <InviteButton />
        <CollabButton open={collabOpen} onToggle={() => set({ collabOpen: !collabOpen })} />
        <AccountButton />
      </div>
    </header>
  );
}

/** The open world: its name, and the way out (back to the start to open another one). */
function WorldMenu() {
  const level = useApp((s) => s.level);
  const demo = useApp((s) => s.demo);
  const guestOf = useApp((s) => s.guestOf);
  const m = usePopover();
  const name = level?.name ?? "World";
  const sub = demo ? "Demo island · generated terrain" : `Minecraft ${level?.version ?? ""}${level?.modded ? " · modded" : ""}${guestOf ? ` · ${guestOf}'s world` : ""}`;
  return (
    <div className="mb-world" ref={m.ref}>
      <button className="btn world-btn" aria-haspopup="menu" aria-expanded={m.open} onClick={m.toggle} title={`${name} · ${sub}`}>
        <WorldTile name={name} />
        <span className="world-name">{name}</span>
        <ChevronDown className="chev" size={14} aria-hidden="true" />
      </button>
      {m.open && (
        <div className="menu world-menu" role="menu">
          <div className="menu-head">
            <WorldTile name={name} size={36} />
            <div className="grow">
              <b>{name}</b>
              <span className="caption">{sub}</span>
            </div>
          </div>
          <div className="menu-sep" />
          <button role="menuitem" className="menu-item" onClick={() => { m.close(); controller.closeWorld(); }} title="Close world">
            <X size={15} aria-hidden="true" />
            <span className="grow">Close world<span className="menu-sub">Back to the start, to open another one</span></span>
          </button>
        </div>
      )}
    </div>
  );
}

function DimensionMenu() {
  const dims = useApp((s) => s.dimensions);
  const dim = useApp((s) => s.dimension);
  const set = useApp((s) => s.set);
  const m = usePopover();
  const cur = dimensionInfo(dim);
  const many = dims.length > 1;
  return (
    <div className="mb-dim" ref={m.ref}>
      <button className="btn btn-pill dim-pill" aria-haspopup={many ? "menu" : undefined} aria-expanded={many ? m.open : undefined} disabled={!many} onClick={m.toggle} title={many ? "Dimension" : "This world has one dimension"} aria-label={`Dimension: ${cur.label}`}>
        <i className="dim-dot" style={{ background: cur.color }} />
        {cur.label}
        {many && <ChevronDown className="chev" size={14} aria-hidden="true" />}
      </button>
      {m.open && (
        <div className="menu dim-menu" role="menu">
          {dims.map((d) => {
            const info = dimensionInfo(d.id);
            return (
              <button key={d.id} role="menuitemradio" aria-checked={d.id === dim} className="menu-item" onClick={() => { set({ dimension: d.id }); m.close(); }}>
                <i className="dim-dot" style={{ background: info.color }} />
                <span className="grow">{info.label}</span>
                {d.id === dim && <Check size={14} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Camera, what's drawn, slice, time of day, detail. */
function ViewBar() {
  const topDown = useApp((s) => s.topDown);
  const showLabels = useApp((s) => s.showLabels);
  const showLines = useApp((s) => s.showLines);
  const set = useApp((s) => s.set);
  return (
    <div className="viewbar" role="toolbar" aria-label="View">
      <div className="vb-group" role="group" aria-label="Camera">
        <button className="vb-btn cam" aria-pressed={!topDown} onClick={() => set({ topDown: false })} title="Tilted 3D view">3D</button>
        <button className="vb-btn cam" aria-pressed={topDown} onClick={() => set({ topDown: true })} title="Straight top-down view, north up"><span>Top<span className="cam-long">-down</span></span></button>
      </div>
      <span className="vb-sep" aria-hidden="true" />
      <button className="vb-btn collapsible" aria-pressed={showLabels} onClick={() => set({ showLabels: !showLabels })} title="Name tags on the map"><Tag size={14} aria-hidden="true" />Labels</button>
      <button className="vb-btn collapsible" aria-pressed={showLines} onClick={() => set({ showLines: !showLines })} title="Outline lines of paths, areas and buildings (the selected object always shows its lines)"><Spline size={14} aria-hidden="true" />Lines</button>
      <SliceControl />
      <span className="vb-sep" aria-hidden="true" />
      <TimeControl />
      <ViewOptions />
    </div>
  );
}

function SliceControl() {
  const on = useApp((s) => s.cutEnabled);
  const y = useApp((s) => s.cutY);
  const set = useApp((s) => s.set);
  const toggle = (next: boolean) => set({ cutEnabled: next, cutY: next ? Math.round(useApp.getState().hover?.y ?? y) : y });
  return (
    <div className={`slider slice${on ? " on" : ""}`} title="Cut the world at a height to see inside buildings and caves">
      <label className="vb-btn collapsible slice-toggle" aria-pressed={on}>
        <input type="checkbox" checked={on} onChange={(e) => toggle(e.target.checked)} aria-label="Slice the world at a height" />
        <Layers size={14} aria-hidden="true" />Slice Y
      </label>
      {on && (
        <span className="slice-controls">
          <input type="range" min={-64} max={320} value={y} onChange={(e) => set({ cutY: +e.target.value })} aria-label="Slice height" />
          <input className="num" type="number" min={-64} max={320} value={y} onChange={(e) => set({ cutY: +e.target.value })} aria-label="Slice height (Y)" />
        </span>
      )}
    </div>
  );
}

function ViewOptions() {
  const rd = useApp((s) => s.renderDistance);
  const set = useApp((s) => s.set);
  const m = usePopover();
  return (
    <div className="vb-more" ref={m.ref}>
      <button className="vb-btn icon" aria-pressed={m.open} aria-haspopup="dialog" aria-expanded={m.open} onClick={m.toggle} title="View options" aria-label="View options"><SlidersHorizontal size={14} aria-hidden="true" /></button>
      {m.open && (
        <div className="menu view-menu" role="dialog" aria-label="View options">
          <div className="menu-field">
            <div className="row between"><span className="field-label">Detail distance</span><span className="mono tabular">{rd} chunks</span></div>
            <input type="range" min={2} max={24} value={rd} onChange={(e) => set({ renderDistance: +e.target.value })} aria-label="Detail distance in chunks" />
            <span className="field-hint">How far 3D blocks reach around the middle of the view. Lower it if the map feels slow.</span>
          </div>
        </div>
      )}
    </div>
  );
}
